import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { FormEvent, useState } from 'react'
import writeXlsxFile, { Row, SheetData } from 'write-excel-file'
import { get } from '../../../lib/http'
import extractDataForm from '../../../utilities/extractDataForm'

import { DataForm, ReportCourtesiesLocalGuide } from './types'

export default function usePrintCourtesiesLocalGuide() {
    const toast = useToast()
    const [isLoading, setIsLoading] = useState(false)

    const head: (...values: string[]) => Row[number][] = (...values) =>
        values.map<Row[number]>(val => ({
            value: val,
            fontWeight: 'bold',
            wrap: true,
            alignVertical: 'center',
            align: 'left',
            fontSize: 11
        }))

    function setCourtesiesLocalGuide(
        data: ReportCourtesiesLocalGuide[],
        rows: SheetData
    ) {
        data.forEach(item => {
            rows.push([
                {
                    value: item.date,
                    type: String
                },
                {
                    value: item.order_id,
                    type: Number
                },
                {
                    value: item.category,
                    type: String
                },
                {
                    value: item.product,
                    type: String
                },
                {
                    value: item.quantity,
                    type: Number
                },
                {
                    value: item.original_price,
                    type: Number,
                    align: 'right',
                    format: '#,##0.00'
                },
                {
                    value: item.local_guide,
                    type: String
                },
                {
                    value: item.courtesy_sale,
                    type: String
                },
                {
                    value: item.reason,
                    type: String
                }
            ])
        })
    }

    async function submit(ev: FormEvent<HTMLFormElement>) {
        ev.preventDefault()
        const form = extractDataForm<DataForm>(ev.target)
        const startDate = dayjs
            .tz(form.startDate + ' 00:00:00', 'America/Lima')
            .toISOString()
        const endDate = dayjs
            .tz(form.endDate + ' 23:59:59', 'America/Lima')
            .toISOString()

        const rows: SheetData = [
            [],
            [
                {
                    value: 'Reporte de Cortesías para Guías Locales',
                    fontWeight: 'bold',
                    span: 9,
                    alignVertical: 'center',
                    align: 'center',
                    fontSize: 16
                }
            ],
            [
                {
                    value: `De: ${dayjs(startDate).format(
                        'DD/MM/YYYY'
                    )} - Hasta ${dayjs(endDate).format('DD/MM/YYYY')}`,
                    span: 9,
                    align: 'center'
                }
            ],
            [],
            head(
                ...[
                    'Fecha y Hora',
                    'Id Pedido',
                    'Categoría',
                    'Producto',
                    'Cantidad',
                    'Precio Original (S/)',
                    'Guía Local',
                    '¿Fue Venta Cortesía?',
                    'Motivo de Venta Cortesía'
                ]
            )
        ]

        setIsLoading(true)

        const { data, error } = await get<ReportCourtesiesLocalGuide[]>(
            `/api/reports/courtesies_local_guide?start_date=${startDate}&end_date=${endDate}`
        )

        if (error) {
            toast({
                title: 'Hubo un error al cargar los datos',
                status: 'error',
                position: 'top'
            })
            return
        }

        setCourtesiesLocalGuide(data.result, rows)

        await writeXlsxFile(rows, {
            fileName: `Reporte_de_cortesias_para_guiaslocales_${dayjs(
                startDate
            ).format('DD/MM/YYYY')}_${dayjs(endDate).format(
                'DD/MM/YYYY'
            )}.xlsx`,
            columns: [25, 15, 25, 30, 10, 20, 30, 25, 35].map(width => ({
                width
            })),
            fontSize: 10,
            fontFamily: 'Calibri'
        })

        setIsLoading(false)
    }

    return {
        submit,
        isLoading
    }
}
